import { useEffect, useState } from "react";
import { Calendar, Clock, Loader2, MapPin, MessageSquare } from "lucide-react";
import { fetchShopList } from "../api.js";
import ImageWithFallback from "../components/ImageWithFallback.jsx";

const FEATURED_ORGS = ["aurora-hair", "linha-fina", "tres-tesouras"];
//const FEATURED_ORGS = ["galeria-men-salon", "novo-sal-o", "org4"];

const FEATURES = [
  {
    title: "Reservas 24/7",
    description: "Permita que os clientes marquem a qualquer hora, sem chamadas.",
    icon: Calendar,
  },
  {
    title: "Poupe tempo",
    description: "Automatize o agendamento e foque-se no atendimento no salão.",
    icon: Clock,
  },
  {
    title: "Lembretes SMS",
    description: "Envio automático de lembretes no dia anterior à marcação.",
    icon: MessageSquare,
  },
];

export default function LandingPage() {
  const [featuredShops, setFeaturedShops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function loadFeaturedShops() {
      const results = await Promise.allSettled(
        FEATURED_ORGS.map((slug) => fetchShopList(slug))
      );
      if (!active) return;

      const nextShops = results.flatMap((result, index) => {
        if (result.status !== "fulfilled") return [];
        const shop = result.value?.[0];
        if (!shop) return [];
        return [{ ...shop, slug: FEATURED_ORGS[index] }];
      });

      setFeaturedShops(nextShops);
      setLoading(false);
    }

    loadFeaturedShops();
    return () => {
      active = false;
    };
  }, []);

  return (
    <main className="min-h-screen bg-background p-6 md:p-12">
      <div className="max-w-6xl mx-auto space-y-12">
        <section className="bg-card border border-border rounded-lg p-8 md:p-12 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gold mb-5">
            Bonitific
          </h1>
          <p className="text-muted text-lg md:text-xl max-w-3xl mx-auto">
            Plataforma de marcações online para cabeleireiros. Automatize horários,
            reduza chamadas e poupe tempo no dia a dia do salão. Os clientes recebem
            lembretes SMS no dia anterior para reduzir faltas.
          </p>
        </section>

        <section>
          <h2 className="text-2xl md:text-3xl font-semibold text-white mb-5">
            Tudo o que precisa para organizar o salão
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {FEATURES.map((feature) => {
              const Icon = feature.icon;
              return (
                <article
                  key={feature.title}
                  className="bg-card border border-border rounded-lg p-5"
                >
                  <Icon className="w-6 h-6 text-gold mb-3" />
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-muted text-sm">{feature.description}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section>
          <h2 className="text-2xl md:text-3xl font-semibold text-white mb-5">
            Salões em destaque
          </h2>

          {loading ? (
            <div className="bg-card border border-border rounded-lg p-8 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-gold animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {featuredShops.map((shop) => (
                <a
                  key={shop.slug}
                  href={`https://www.bonitific.pt/${shop.slug}`}
                  className="group bg-card border border-border rounded-lg overflow-hidden hover:border-gold/50 transition-all"
                >
                  <div className="aspect-video overflow-hidden border-b border-border">
                    <ImageWithFallback
                      src={shop.image_url}
                      alt={shop.name ? `Foto do salao ${shop.name}` : "Foto do salao"}
                      label={shop.name || "Salao"}
                      variant="salon"
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>

                  <div className="p-4">
                    <h3 className="text-xl font-semibold text-white mb-2 truncate">
                      {shop.name || "Salao"}
                    </h3>

                    {shop.address && (
                      <div className="flex items-start gap-2 text-muted">
                        <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span className="text-sm line-clamp-2">{shop.address}</span>
                      </div>
                    )}
                  </div>
                </a>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
